import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { Auth0Provider } from "@auth0/auth0-react";
import RoadmapPage from "./pages/RoadmapPage";
import WorkbookDetailPage from "./pages/WorkbookDetailPage";
import IdeasInboxPage from "./pages/IdeasInboxPage";
import VisionBoardPage from "./pages/VisionBoardPage";
import MapPage from "./pages/MapPage";
import ShowcaseLoginPage from "./pages/showcase/ShowcaseLoginPage";
import ShowcaseDashboardPage from "./pages/showcase/ShowcaseDashboardPage";
import ProductDrilldownPage from "./pages/showcase/ProductDrilldownPage";
import ShowcaseAboutPage from "./pages/showcase/ShowcaseAboutPage";
import AuthGate from "./components/AuthGate";
import { ThemeProvider } from "./lib/theme";
import { apiClient } from "./lib/api";
import "./index.css";

const AUTH0_DOMAIN = process.env.REACT_APP_AUTH0_DOMAIN || "lifttech.au.auth0.com";
const AUTH0_CLIENT_ID =
  process.env.REACT_APP_AUTH0_CLIENT_ID || "VyfqHVeJmTOxUb8vodKv1LdbRAL6V2Fx";

/**
 * BrandRedirect — bridges the bare `/brand` URL to the static catalogue at
 * `/brand/index.html`. The catalogue is a standalone static HTML page (canon-
 * sourced from `frontend/public/brand/index.html`), not a SPA route, so we use
 * a full-page navigation rather than React Router. Lives OUTSIDE the AuthGate
 * because the catalogue + assets are intentionally public (URL-addressable for
 * any system to consume per `architecture-canon/BRAND/assets/README.md §6`).
 */
function BrandRedirect() {
  useEffect(() => {
    window.location.replace("/brand/index.html");
  }, []);
  return null;
}

function App() {
  return (
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: window.location.origin,
        scope: "openid profile email",
      }}
      cacheLocation="localstorage"
      useRefreshTokens={true}
    >
      <ThemeProvider>
        <BrowserRouter>
          <Routes>
            {/* Public route — bypasses AuthGate so brand assets stay URL-addressable */}
            <Route path="/brand" element={<BrandRedirect />} />
            {/* Public /showcase/* routes — bypass AuthGate for non-engineer humans
                (board, partners, family). Password-gated via /api/showcase/login
                cookie. Added 2026-09-24 per Owner arbitration [C209?] follow-up. */}
            <Route path="/showcase" element={<ShowcaseDashboardPage />} />
            <Route path="/showcase/login" element={<ShowcaseLoginPage />} />
            <Route path="/showcase/about" element={<ShowcaseAboutPage />} />
            <Route path="/showcase/product/:slug" element={<ProductDrilldownPage />} />
            {/* All other routes gated by Auth0 sign-in */}
            <Route
              path="*"
              element={
                <AuthGate
                  apiClient={apiClient}
                  appName="Roadmap"
                  appSubLabel="Master Planner"
                  signInDescription="Sign in with your LiftTech identity to view the live canon roadmap."
                >
                  <Routes>
                    <Route path="/" element={<RoadmapPage />} />
                    <Route path="/board" element={<VisionBoardPage />} />
                    <Route path="/map" element={<MapPage />} />
                    <Route path="/workbook/:id" element={<WorkbookDetailPage />} />
                    <Route path="/ideas" element={<IdeasInboxPage />} />
                  </Routes>
                </AuthGate>
              }
            />
          </Routes>
          <Toaster
            className="roadmap-toast"
            visibleToasts={3}
            position="top-right"
            theme="system"
            toastOptions={{
              classNames: { toast: "border surface-card text-sm" },
            }}
          />
        </BrowserRouter>
      </ThemeProvider>
    </Auth0Provider>
  );
}

export default App;
